import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Email } from '@kula-ai/components';
import type {
  EditorProps,
  EmailCompositionValue,
  EmailProps,
} from '@kula-ai/components';
import type { SelectOption } from '@kula-ai/components';
import {
  applyTemplateMetadataOnChange,
  mapTemplateAttachmentsToEditorItems,
} from '../helpers/templateApply';
import type {
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../communication.types';

function interviewFileToAttachmentItem(
  file: File
): InterviewEditorAttachmentItem {
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    file,
  };
}

export type InterviewEmailEditorVariant = 'calendarInvite' | 'richEmail';

export type TemplatedEmailProps = Omit<
  EmailProps,
  'onChange' | 'templateOptions'
> & {
  templateOptions: SelectOption[];
  value: EmailCompositionValue;
  onChange: (_value: EmailCompositionValue) => void;
  /** Calendar invites: no image/attachment toolbar. Rich email: image + attachment. */
  editorVariant?: InterviewEmailEditorVariant;
  /** ATS template variables (`/ats_comm_templates/variables`) for `{ }` picker in subject + body. */
  variables?: InterviewVariablesByCategory;
  attachments?: InterviewEditorAttachmentItem[];
  onAttachmentsChange?: (_items: InterviewEditorAttachmentItem[]) => void;
  /** Rich email only — `POST /user_image_uploads` via host app (see `useUploadUserInlineImage`). */
  uploadInlineImage?: EditorProps['uploadInlineImage'];
};

/**
 * Wraps Kula `Email`: keeps subject/body aligned with `SelectOption.data` when the template changes,
 * and shows template attachment chips on the editor when present.
 */
export function TemplatedEmail({
  templateOptions,
  value,
  onChange,
  editorProps,
  editorVariant = 'calendarInvite',
  variables,
  attachments,
  onAttachmentsChange,
  uploadInlineImage,
  subjectVariableInputProps,
  ...rest
}: TemplatedEmailProps) {
  const prev = useRef(value);
  useEffect(() => {
    prev.current = value;
  }, [value]);

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const merged = applyTemplateMetadataOnChange(
        prev.current,
        next,
        templateOptions
      );
      prev.current = merged;
      onChange(merged);
    },
    [onChange, templateOptions]
  );

  const templateAttachments = useMemo(() => {
    const opt = templateOptions.find((o) => o.value === value.template);
    return mapTemplateAttachmentsToEditorItems(opt?.data?.attachments);
  }, [templateOptions, value.template]);

  const toolbarFeatures = useMemo(
    () =>
      editorVariant === 'calendarInvite'
        ? { image: false, attachment: false }
        : { image: true, attachment: true },
    [editorVariant]
  );

  const mergedEditorProps = useMemo(() => {
    const fromTemplate = templateAttachments;
    const user = editorVariant === 'richEmail' ? (attachments ?? []) : [];
    const combined =
      fromTemplate.length || user.length
        ? [...fromTemplate, ...user]
        : undefined;

    const attachmentHandlers =
      editorVariant === 'richEmail' && onAttachmentsChange
        ? {
            onAttachFiles: (files: File[]) => {
              const added = files.map(interviewFileToAttachmentItem);
              onAttachmentsChange([...(attachments ?? []), ...added]);
            },
            onRemoveAttachment: (key: string) => {
              const userAtt = attachments ?? [];
              if (!userAtt.some((a) => a.key === key)) {
                return;
              }
              onAttachmentsChange(userAtt.filter((a) => a.key !== key));
            },
          }
        : {};

    const inlineImageProp =
      editorVariant === 'richEmail' && uploadInlineImage
        ? { uploadInlineImage }
        : {};

    return {
      ...editorProps,
      variables: variables ?? editorProps?.variables,
      toolbarFeatures: {
        ...editorProps?.toolbarFeatures,
        ...toolbarFeatures,
      },
      ...(combined?.length ? { attachments: combined } : {}),
      ...attachmentHandlers,
      ...inlineImageProp,
    };
  }, [
    attachments,
    editorProps,
    editorVariant,
    onAttachmentsChange,
    templateAttachments,
    toolbarFeatures,
    uploadInlineImage,
    variables,
  ]);

  const mergedSubjectVariableInputProps = useMemo(
    () =>
      variables && Object.keys(variables).length > 0
        ? { ...subjectVariableInputProps, variables }
        : subjectVariableInputProps,
    [subjectVariableInputProps, variables]
  );

  return (
    <Email
      {...rest}
      editorProps={mergedEditorProps}
      subjectVariableInputProps={mergedSubjectVariableInputProps}
      templateOptions={templateOptions}
      value={value}
      onChange={handleChange}
    />
  );
}
